<template>
  <!-- persistent once something has been put in: a tap beside the dialog, or
       the phone's Back, used to close it and the next opening started empty. -->
  <v-dialog v-model="isVisible" :max-width="plate ? 680 : 800" :persistent="hasContent">
    <!-- Seaded -> Dialoogid uues kujunduses, agreed on a mockup first: where we
         are in the head, märkus or hooldus as two buttons, the deadlines as
         chips, the steps in two columns with Märgi kõik, photos as pictures,
         the object's own on/off and what the customer sees, and a button that
         says what it adds. The same request as the old form. -->
    <form v-if="plate" class="dlg" novalidate @submit.prevent="submitPlate()">
      <div class="dlg-head">
        <div>
          <div v-if="caption" class="dlg-cap">{{ caption }}</div>
          <h2 class="dlg-title">Lisa märkus</h2>
        </div>
        <button type="button" class="dlg-x" aria-label="Sulge" @click="isVisible = false"><v-icon>mdi-close</v-icon></button>
      </div>
      <div class="dlg-body">
        <div>
          <span class="dlg-lbl">Mis see on?</span>
          <div class="dlg-kind" role="group" aria-label="Liik">
            <button type="button" :aria-pressed="String(!form.service)" :disabled="submitLoading" @click="form.service = false">
              <span class="dlg-ic"><v-icon size="18">mdi-note-edit-outline</v-icon></span><span><b>Märkus</b><span>Mis tehti või mida märgati.</span></span>
            </button>
            <button type="button" :aria-pressed="String(!!form.service)" :disabled="submitLoading" @click="form.service = true">
              <span class="dlg-ic"><v-icon size="18">mdi-check</v-icon></span><span><b>Hooldus</b><span>Täidab lepingu tähtaja, sammud kirja.</span></span>
            </button>
          </div>
        </div>
        <div class="dlg-row2">
          <label>
            <span class="dlg-lbl">Kuupäev <span class="dlg-req">*</span></span>
            <date-field v-model="form.entryDate" plain block class="dlg-in" :class="{ bad: tried && !form.entryDate }" :disabled="submitLoading" />
          </label>
          <div v-if="form.service">
            <span class="dlg-lbl">Täidab tähtaja</span>
            <div v-if="intervals.length" class="dlg-chips" role="group" aria-label="Tähtaeg">
              <!-- Oldest first, as the old list's default already was. -->
              <button
                v-for="interval in intervalsOldestFirst"
                :key="interval.id"
                type="button"
                class="dlg-chip"
                :aria-pressed="String(form.intervalId === interval.id)"
                :disabled="submitLoading"
                @click="form.intervalId = form.intervalId === interval.id ? undefined : interval.id"
              >{{ interval.title }}<i v-if="isLate(interval)">hiljaks</i></button>
            </div>
            <span v-else class="dlg-none">Lahtisi tähtaegu pole.</span>
          </div>
        </div>
        <div v-if="form.service && services.length">
          <div class="dlg-steps-head">
            <span class="dlg-lbl">Tehtud sammud ({{ form.services.length }} / {{ services.length }})</span>
            <button type="button" class="dlg-link" :disabled="submitLoading" @click="toggleAllSteps()">{{ form.services.length === services.length ? 'Võta kõik maha' : 'Märgi kõik' }}</button>
          </div>
          <ul class="dlg-steps">
            <li v-for="service in services" :key="service.id">
              <label><input v-model="form.services" type="checkbox" :value="service.id" :disabled="submitLoading"><em>{{ service.order }}.</em>{{ service.description }}</label>
            </li>
          </ul>
        </div>
        <label>
          <span class="dlg-lbl">Kommentaar <span class="dlg-req">*</span></span>
          <textarea v-model="form.description" class="dlg-in" :class="{ bad: tried && !form.description.trim() }" :disabled="submitLoading" placeholder="Mis tehti, mida märgati, mis jäi tegemata"></textarea>
          <div v-if="tried && !form.description.trim()" class="dlg-err">Kommentaar on kohustuslik</div>
        </label>
        <div>
          <span class="dlg-lbl">Fotod</span>
          <div class="dlg-photos">
            <span v-for="(file, i) in form.files" :key="i" class="dlg-ph" :style="previews[i] ? { backgroundImage: 'url(' + previews[i] + ')' } : null">
              <template v-if="!previews[i]">{{ file.name }}</template>
              <button type="button" aria-label="Eemalda foto" :disabled="submitLoading" @click="removeFile(i)">✕</button>
            </span>
            <label class="dlg-ph-add"><v-icon size="20">mdi-camera</v-icon>Tee pilt
              <input type="file" accept="image/*" capture="environment" :disabled="submitLoading" @change="pickFiles"></label>
            <label class="dlg-ph-add"><v-icon size="20">mdi-image-multiple</v-icon>Failidest
              <input type="file" accept="image/*" multiple :disabled="submitLoading" @change="pickFiles"></label>
          </div>
        </div>
        <!-- Not the note's: POST /object/:id/note sets the object's own
             active flag, which is how a boiler switched off is written down. -->
        <div class="dlg-box">
          <span class="dlg-lbl" style="margin: 0">Objekt</span>
          <button type="button" class="dlg-sw" role="switch" :aria-checked="String(!!form.active)" :disabled="submitLoading" @click="form.active = !form.active">
            <span class="dlg-track" />{{ form.active ? 'Objekt on aktiivne (katel kasutuses)' : 'Objekt mitteaktiivne (katel välja lülitatud)' }}
          </button>
        </div>
        <div class="dlg-box">
          <span class="dlg-lbl" style="margin: 0">Kliendiportaalis</span>
          <button type="button" class="dlg-sw" role="switch" :aria-checked="String(showToClient)" :disabled="submitLoading" @click="showToClient = !showToClient">
            <span class="dlg-track" />Klient näeb seda märkust
          </button>
          <button type="button" class="dlg-sw" role="switch" :aria-checked="String(showToClient && showPhotosToClient)" :disabled="submitLoading || !showToClient" @click="showPhotosToClient = !showPhotosToClient">
            <span class="dlg-track" />Klient näeb fotosid
          </button>
        </div>
      </div>
      <div class="dlg-foot">
        <span class="dlg-grow">{{ plateSummary }}</span>
        <button type="button" class="dlg-btn dlg-btn--ghost" :disabled="submitLoading" @click="isVisible = false">Loobu</button>
        <button type="submit" class="dlg-btn" :disabled="submitLoading">{{ submitLoading ? 'Salvestan…' : (form.service ? 'Lisa hooldus' : 'Lisa märkus') }}</button>
      </div>
    </form>
    <v-card v-else>
      <v-toolbar color="primary" theme="dark">
        <v-toolbar-title>Lisa uus märkus</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="isVisible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-0">
        <v-container>
          <v-row dense>
            <v-col cols="12">
              <v-form ref="observer" @submit.prevent>
                <datepicker
                  v-model="form.entryDate"
                  label="Kuupäev"
                  :rules="[rules.required]"
                  dense
                  outlined
                  :disabled="submitLoading"
                />
                <v-checkbox
                  class="mt-0"
                  density="compact"
                  v-model="form.active"
                  label="Aktiivne"
                  :disabled="submitLoading"
                />
                <v-checkbox
                  class="mt-0"
                  density="compact"
                  v-model="form.service"
                  label="Hooldus"
                  :disabled="submitLoading"
                />
                <v-checkbox
                  class="mt-0"
                  density="compact"
                  v-model="showToClient"
                  label="Näita kliendile"
                  :disabled="submitLoading"
                />
                <v-checkbox
                  v-if="showToClient"
                  class="mt-0"
                  density="compact"
                  v-model="showPhotosToClient"
                  label="Näita fotosid kliendile"
                  :disabled="submitLoading"
                />
                <v-select
                  v-if="form.service"
                  label="Hoolduse intervall"
                  v-model="form.intervalId"
                  :items="intervals"
                  item-title="title"
                  item-value="id"
                  density="compact"
                  variant="outlined"
                  :disabled="submitLoading"
                  hide-details="auto"
                />
                <div v-if="form.service" class="mb-5 services">
                  <v-checkbox
                    v-for="service in services"
                    :key="service.id"
                    v-model="form.services"
                    :label="service.order + '.' + service.description"
                    :value="service.id"
                    density="compact"
                    hide-details="auto"
                    class="pa-0"
                    :disabled="submitLoading"
                  />
                </div>
                <v-textarea
                  density="compact"
                  variant="outlined"
                  v-model="form.description"
                  label="Kommentaar"
                  :disabled="submitLoading"
                />
                <div class="text-caption text-grey mb-1">Fotod</div>
                <photo-picker :disabled="submitLoading" @files="addFiles" />
                <div v-if="form.files.length" class="mt-2">
                  <v-chip
                    v-for="(file, i) in form.files"
                    :key="i"
                    class="mr-1 mb-1"
                    closable
                    size="small"
                    :disabled="submitLoading"
                    @click:close="form.files.splice(i, 1)"
                  >{{ file.name }}</v-chip>
                </div>
              </v-form>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-btn :loading="submitLoading" @click="_submit()" block color="primary" variant="elevated">Lisa</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api'
import formMixin from '@/plugins/formMixin'
import visibleMixin from '@/plugins/visibleMixin'
import Datepicker from '@/components/input/Datepicker.vue'
import PhotoPicker from '@/components/PhotoPicker.vue'
import rules from '@/plugins/rules'
import dayjs from 'dayjs'
import { last } from 'lodash'
import { mapState } from 'vuex'
import '@/styles/dialog-plate.css'

const empty = {
  entryDate: dayjs().format('YYYY-MM-DD'),
  active: true,
  service: false,
  services: [],
  description: '',
  files: [],
  calendarId: undefined,
  intervalId: undefined,
  // Notes and their photos are visible to the customer by default, for both märkused and
  // hooldused. An employee hides either one per note when needed.
  hiddenFromClient: false,
  photosHiddenFromClient: false
}

export default {
  mixins: [ formMixin, visibleMixin ],
  components: { Datepicker, PhotoPicker },
  props: {
    object: {
      required: false
    },
    objectId: {
      required: true
    },
    event: {
      required: false
    }
  },
  data () {
    return {
      form: {
        ...empty
      },
      rules,
      intervals: [],
      services: [],
      // The new look: picture previews for form.files, and whether Lisa was
      // pressed (the required fields only say so after that).
      previews: [],
      tried: false
    }
  },
  watch: {
    isVisible () {
      this.refresh()
    },
    objectId () {
      this.refresh()
    },
    object (object) {
      if (object) {
        this.form.active = object.active
      }
    },
    'form.service' (isService) {
      // Both types are visible to the customer by default, so switching between märkus and
      // hooldus leaves the visibility choice alone; it only drives the interval selection.
      if (isService && this.intervals && this.intervals.length) {
        this.form.intervalId = last(this.intervals).id
      } else {
        this.form.intervalId = undefined
      }
    }
  },
  computed: {
    // Something typed, ticked or attached that closing would throw away.
    hasContent () {
      const form = this.form || {}
      return !!((form.description && String(form.description).trim()) ||
        (form.files && form.files.length) || (form.services && form.services.length))
    },
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.dialogPlate)
    },
    // Where the note goes: the address, the contract and how often it is
    // serviced, or the töökäsk it was opened from.
    caption () {
      const o = this.object || {}
      const parts = [o.fullAddress ? this.$address(o.fullAddress) : (o.address || '')]
      if (o.contract) parts.push('Leping ' + o.contract)
      if (this.event && this.event.startDate) parts.push('Töökäsk ' + dayjs(this.event.startDate).format('DD.MM.YYYY'))
      return parts.filter(Boolean).join(' · ')
    },
    // /object/:id/interval answers newest first.
    intervalsOldestFirst () {
      return this.intervals.slice().reverse()
    },
    plateSummary () {
      const parts = []
      if (this.form.service) parts.push(this.form.services.length + ' sammu')
      if (this.form.files.length) parts.push(this.form.files.length + (this.form.files.length === 1 ? ' foto' : ' fotot'))
      parts.push(this.showToClient ? 'klient näeb' : 'kliendi eest peidus')
      if (this.object && this.object.active && !this.form.active) parts.push('objekt lülitatakse välja')
      if (this.object && !this.object.active && this.form.active) parts.push('objekt lülitatakse sisse')
      return parts.join(' · ')
    },
    successMessage () {
      return 'Märkus edukalt lisatud'
    },
    // The stored flags are "hide" flags, but the form offers the choice the other way
    // round — "show to the customer" — so both checkboxes start ticked (visible by default).
    showToClient: {
      get () { return !this.form.hiddenFromClient },
      set (value) { this.form.hiddenFromClient = !value }
    },
    showPhotosToClient: {
      get () { return !this.form.photosHiddenFromClient },
      set (value) { this.form.photosHiddenFromClient = !value }
    }
  },
  methods: {
    isLate (interval) {
      return dayjs(interval.date).isBefore(dayjs(), 'day')
    },
    toggleAllSteps () {
      this.form.services = this.form.services.length === this.services.length ? [] : this.services.map(service => service.id)
    },
    pickFiles (event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      this.addFiles(files)
    },
    removeFile (i) {
      if (this.previews[i]) URL.revokeObjectURL(this.previews[i])
      this.previews.splice(i, 1)
      this.form.files.splice(i, 1)
    },
    clearPreviews () {
      this.previews.forEach(url => url && URL.revokeObjectURL(url))
      this.previews = []
    },
    // The new look has no v-form to validate; the server's own rules (a date
    // and a comment) are said here first.
    async submitPlate () {
      this.tried = true
      if (!this.form.entryDate || !this.form.description.trim() || this.submitLoading) return
      try {
        this.submitLoading = true
        await this.handleForm()
        this.showNotification({ text: this.successMessage, color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.submitLoading = false
      }
    },
    addFiles (files) {
      // PhotoPicker hands over one pick at a time and clears its own inputs, so
      // accumulate into form.files to support adding photos one at a time.
      if (files && files.length) {
        this.form.files = [...this.form.files, ...files]
        this.previews = [...this.previews, ...Array.from(files).map(file => {
          try { return /^image\//.test(file.type) ? URL.createObjectURL(file) : null } catch (e) { return null }
        })]
      }
    },
    async handleForm () {
      // The note is saved once. When its photos then fail to upload (a big
      // file, a lost connection) the dialog stays open with an error — and a
      // second Salvesta used to save the whole note again, a second hooldus
      // using up a second interval. Now it only sends the photos again.
      if (!this.createdNoteId) {
        const request = {
          ...this.form
        }
        const { note } = await api.createNote(this.objectId, request)
        this.createdNoteId = note.id
        this.$emit('created')
      }
      if (this.form.files && this.form.files.length) {
        try {
          await api.uploadNotePhotos(this.objectId, this.createdNoteId, this.form.files)
        } catch (err) {
          throw new Error('Märkus on salvestatud, aga fotode üleslaadimine ebaõnnestus (' + ((err && err.message) || 'ühenduse viga') +
            '). Vajuta uuesti Salvesta, et saata ainult fotod.')
        }
        this.$emit('created')
      }
      this.createdNoteId = null
      this._hide()
      return true
    },
    refresh () {
      if (!this.isVisible) {
        return
      }
      this.createdNoteId = null
      // The date is today's when the dialog opens, not the day the page was
      // loaded: a tab left open overnight offered yesterday.
      this.form = { ...empty, entryDate: dayjs().format('YYYY-MM-DD'), files: [], services: [] }
      this.tried = false
      this.clearPreviews()
      this.form.active = this.object ? this.object.active : false
      if (this.event) {
        this.form.calendarId = this.event.id
        this.form.entryDate = dayjs(this.event.startDate).format('YYYY-MM-DD')
      }
      this._reset()
      this.getIntervals()
    },
    async getIntervals () {
      this.intervals = (await api.getObjectIntervals(this.objectId, { unused: true, limit: 100 })).intervals
    },
    async getServices () {
      this.services = (await api.getServices()).services
    }
  },
  mounted () {
    this.getServices()
  },
  beforeUnmount () {
    this.clearPreviews()
  }
}
</script>
